//* 장례식장별 DN(부고브랜드 × 장례식장 → 꽃집) 연결 현황 조회 훅
//* DN이 없으면 그 장례식장 부고에서 화환 주문 목록이 아예 뜨지 않으므로 미연결 장례식장을 찾는 용도
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { useSimpleBugoBrandListHook } from '@shared/api/bugoBrand/bugoBrand.hook';
import { simpleBugoBrandListStateSelector } from '@shared/api/bugoBrand/bugoBrand.selector';
import { DistributionNetwork } from '@shared/api/distributionNetwork/distributionNetwork.interface';
import { useFuneralHomeInfoListHook } from '@shared/api/funeralHomeInfo/funeralHomeInfo.hook';
import { FuneralHomeInfoListStateSelector } from '@shared/api/funeralHomeInfo/funeralHomeInfo.selector';
import { queryString } from '@shared/hooks/recoil-query';
import { api_ } from '@shared/plugIn/axios';

//* DN 개수는 (브랜드수 × 장례식장수)까지 커질 수 있어 페이지 단위로 끊어 조회
const DN_FETCH_LIMIT = 20000;
//* 무한루프 방지용 최대 페이지 수
const DN_FETCH_MAX_PAGE = 20;

//* 개수가 많아 id 두 개만 받아온다 (_id도 제외)
type DnIdPair = Pick<DistributionNetwork, 'funeralHomeInfo' | 'bugoBrand'>;

export type FuneralHomeDnStatus = 'none' | 'partial' | 'full';

export type FuneralHomeDnInfo = {
  //* 살아있는 부고브랜드 중 연결된 개수
  connectedCount: number;
  brandCount: number;
  status: FuneralHomeDnStatus;
};

const NONE_DN_INFO: FuneralHomeDnInfo = {
  connectedCount: 0,
  brandCount: 0,
  status: 'none',
};

//* 관리자 홈 배지용 캐시. DN은 장례식장/브랜드 생성 때만 변해서 짧은 TTL이면 충분
let dnMapCache: { map: Map<string, Set<string>>; fetchedAt: number } | null = null;
const DN_MAP_CACHE_TTL_MS = 5 * 60 * 1000;

//* funeralHomeInfoId -> 연결된 bugoBrandId Set 수집 (페이지 단위 조회)
const fetchConnectedBrandMap = async (): Promise<Map<string, Set<string>>> => {
  const map = new Map<string, Set<string>>();
  for (let page = 0; page < DN_FETCH_MAX_PAGE; page += 1) {
    const { data } = await api_.get<DnIdPair[]>(
      `admin/distribution-network/?${queryString({
        options: {
          limit: DN_FETCH_LIMIT,
          skip: page * DN_FETCH_LIMIT,
          sort: { _id: 1 },
        },
      })}&select=${encodeURIComponent('funeralHomeInfo bugoBrand -_id')}`,
    );
    data.forEach((dn) => {
      if (!dn.funeralHomeInfo) return;
      const brandSet = map.get(dn.funeralHomeInfo) ?? new Set<string>();
      //* 브랜드가 삭제된 고아 DN은 연결로 치지 않음
      if (dn.bugoBrand) brandSet.add(dn.bugoBrand);
      map.set(dn.funeralHomeInfo, brandSet);
    });
    if (data.length < DN_FETCH_LIMIT) break;
  }
  //* 홈 배지 훅이 재사용할 세션 캐시 — 관리 페이지가 새로 조회할 때마다 최신으로 갱신됨
  dnMapCache = { map, fetchedAt: Date.now() };
  return map;
};

export const useFuneralHomeDnStatusHook = () => {
  useSimpleBugoBrandListHook();
  const simpleBugoBrandState = useRecoilValue(simpleBugoBrandListStateSelector);

  //* funeralHomeInfoId -> 연결된 bugoBrandId Set
  const [connectedBrandMap, setConnectedBrandMap] = useState<Map<
    string,
    Set<string>
  > | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  const fetchDnList = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setConnectedBrandMap(await fetchConnectedBrandMap());
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDnList();
  }, [fetchDnList]);

  const brandIdSet = useMemo(() => {
    if (simpleBugoBrandState.status !== 'success' || !simpleBugoBrandState.data)
      return null;
    return new Set(simpleBugoBrandState.data.map((brand) => brand._id));
  }, [simpleBugoBrandState.status, simpleBugoBrandState.data]);

  //* funeralHomeInfoId -> DN 연결 현황
  const dnInfoMap = useMemo(() => {
    const map = new Map<string, FuneralHomeDnInfo>();
    if (!connectedBrandMap || !brandIdSet) return map;
    const brandCount = brandIdSet.size;
    connectedBrandMap.forEach((brandSet, funeralHomeInfoId) => {
      let connectedCount = 0;
      brandSet.forEach((brandId) => {
        if (brandIdSet.has(brandId)) connectedCount += 1;
      });
      map.set(funeralHomeInfoId, {
        connectedCount,
        brandCount,
        status:
          connectedCount === 0
            ? 'none'
            : connectedCount < brandCount
            ? 'partial'
            : 'full',
      });
    });
    return map;
  }, [connectedBrandMap, brandIdSet]);

  const ready = !!connectedBrandMap && !!brandIdSet;

  //* DN이 하나도 없는 장례식장은 map에 아예 없으므로 기본값(none)으로 처리
  const getDnInfo = useCallback(
    (funeralHomeInfoId: string): FuneralHomeDnInfo => {
      return (
        dnInfoMap.get(funeralHomeInfoId) ?? {
          ...NONE_DN_INFO,
          brandCount: brandIdSet?.size ?? 0,
        }
      );
    },
    [dnInfoMap, brandIdSet],
  );

  return {
    ready,
    loading: loading || simpleBugoBrandState.status === 'loading',
    error,
    brandCount: brandIdSet?.size ?? 0,
    getDnInfo,
    refetch: fetchDnList,
  };
};

//* 관리자 홈 배지용: DN이 하나도 없는(none) 장례식장 수만 가볍게 계산.
//* DN 목록은 세션 캐시(TTL 5분)를 우선 사용하고, 실패 시 배지를 숨긴다(null).
export const useFuneralHomeDnNoneCountHook = (): number | null => {
  useSimpleBugoBrandListHook();
  useFuneralHomeInfoListHook();
  const simpleBugoBrandState = useRecoilValue(simpleBugoBrandListStateSelector);
  const funeralHomeInfoListState = useRecoilValue(FuneralHomeInfoListStateSelector);

  const [connectedBrandMap, setConnectedBrandMap] = useState<Map<
    string,
    Set<string>
  > | null>(() =>
    dnMapCache && Date.now() - dnMapCache.fetchedAt < DN_MAP_CACHE_TTL_MS
      ? dnMapCache.map
      : null,
  );

  useEffect(() => {
    if (connectedBrandMap) return;
    let alive = true;
    fetchConnectedBrandMap()
      .then((map) => {
        if (alive) setConnectedBrandMap(map);
      })
      .catch((err) => {
        console.error(err);
      });
    return () => {
      alive = false;
    };
  }, [connectedBrandMap]);

  return useMemo(() => {
    if (!connectedBrandMap) return null;
    if (simpleBugoBrandState.status !== 'success' || !simpleBugoBrandState.data)
      return null;
    if (
      funeralHomeInfoListState.status !== 'success' ||
      !funeralHomeInfoListState.data
    )
      return null;
    const brandIdSet = new Set(simpleBugoBrandState.data.map((brand) => brand._id));
    let noneCount = 0;
    funeralHomeInfoListState.data.forEach((funeralHomeInfo) => {
      const brandSet = connectedBrandMap.get(funeralHomeInfo._id);
      const hasLiveBrand =
        !!brandSet &&
        Array.from(brandSet).some((brandId) => brandIdSet.has(brandId));
      if (!hasLiveBrand) noneCount += 1;
    });
    return noneCount;
  }, [
    connectedBrandMap,
    simpleBugoBrandState.status,
    simpleBugoBrandState.data,
    funeralHomeInfoListState.status,
    funeralHomeInfoListState.data,
  ]);
};
